import authService from './authService';
import { ensureOk, readData, readList, toQuery } from './apiResponse';
import type {
  ConnectionReport,
  FormatsResponse,
  HashtagGroup,
  Idea,
  IdeaKind,
  Network,
  OAuthNetwork,
  Pillar,
  PillarBalance,
  PostFilter,
  SaveAccountRequest,
  SaveHashtagGroupRequest,
  SaveIdeaRequest,
  SavePillarRequest,
  SavePostRequest,
  SocialAccount,
  SocialGrid,
  SocialPost,
  SocialSummary,
} from '@/types/social';

/** Coleções que o backend pode mandar como `null`. */
const normalize = (p: SocialPost): SocialPost => ({
  ...p,
  targets: p?.targets ?? [],
  media: p?.media ?? [],
  issues: p?.issues ?? [],
});

/** Redes sociais — ver docs/API.md do marketingbackgo, seção "Redes sociais". */
class SocialService {
  private async api() {
    return authService.getApiClient();
  }

  private formatsCache: Promise<FormatsResponse> | null = null;

  /** GET /social/formats (regras por formato; guardado em memória — muda só com deploy). */
  formats(): Promise<FormatsResponse> {
    if (!this.formatsCache) {
      this.formatsCache = (async () => {
        const res = await (await this.api()).get('/social/formats');
        return readData<FormatsResponse>(res, 'Falha ao carregar os formatos das redes');
      })();
      this.formatsCache.catch(() => {
        this.formatsCache = null;
      });
    }
    return this.formatsCache;
  }

  async summary(): Promise<SocialSummary> {
    const res = await (await this.api()).get('/social/summary');
    return readData<SocialSummary>(res, 'Falha ao carregar o resumo das redes sociais');
  }

  // ------------------------------------------------------------ posts

  async listPosts(f: PostFilter = {}): Promise<SocialPost[]> {
    const q = toQuery({
      from: f.from,
      to: f.to,
      status: f.status?.join(','),
      network: f.network,
      account_id: f.account_id,
      pillar_id: f.pillar_id,
      media_id: f.media_id,
      search: f.search,
      scheduled: f.scheduled,
      unscheduled: f.unscheduled,
    });
    const res = await (await this.api()).get(`/social/posts${q}`);
    return (await readList<SocialPost>(res, 'Falha ao buscar os posts')).map(normalize);
  }

  async grid(network: Network, accountId?: number): Promise<SocialGrid> {
    const res = await (await this.api()).get(`/social/grid${toQuery({ network, account_id: accountId })}`);
    const g = await readData<SocialGrid>(res, 'Falha ao carregar a grade');
    return {
      ...g,
      planned: (g.planned ?? []).map(normalize),
      unscheduled: (g.unscheduled ?? []).map(normalize),
      feed: g.feed ?? [],
      balance: g.balance ?? [],
    };
  }

  async reorder(postIds: number[]): Promise<SocialPost[]> {
    const res = await (await this.api()).post('/social/grid/reorder', { post_ids: postIds });
    return (await readList<SocialPost>(res, 'Falha ao reordenar a grade')).map(normalize);
  }

  async balance(from?: string, to?: string): Promise<PillarBalance[]> {
    const res = await (await this.api()).get(`/social/balance${toQuery({ from, to })}`);
    return readList<PillarBalance>(res, 'Falha ao calcular o equilíbrio dos pilares');
  }

  async getPost(id: number): Promise<SocialPost> {
    const res = await (await this.api()).get(`/social/posts/${id}`);
    return normalize(await readData<SocialPost>(res, 'Falha ao carregar o post'));
  }

  async createPost(req: SavePostRequest): Promise<SocialPost> {
    const res = await (await this.api()).post('/social/posts', req);
    return normalize(await readData<SocialPost>(res, 'Falha ao criar o post'));
  }

  async updatePost(id: number, req: SavePostRequest): Promise<SocialPost> {
    const res = await (await this.api()).put(`/social/posts/${id}`, req);
    return normalize(await readData<SocialPost>(res, 'Falha ao salvar o post'));
  }

  async deletePost(id: number): Promise<void> {
    const res = await (await this.api()).delete(`/social/posts/${id}`);
    await ensureOk(res, 'Falha ao excluir o post');
  }

  async duplicate(id: number): Promise<SocialPost> {
    const res = await (await this.api()).post(`/social/posts/${id}/duplicate`);
    return normalize(await readData<SocialPost>(res, 'Falha ao duplicar o post'));
  }

  async schedule(id: number, scheduledAt: string | null): Promise<SocialPost> {
    const res = await (await this.api()).patch(`/social/posts/${id}/schedule`, { scheduled_at: scheduledAt });
    return normalize(await readData<SocialPost>(res, 'Falha ao reagendar o post'));
  }

  private async decision(id: number, action: string, body: object, fallback: string): Promise<SocialPost> {
    const res = await (await this.api()).post(`/social/posts/${id}/${action}`, body);
    return normalize(await readData<SocialPost>(res, fallback));
  }

  submit(id: number, comment?: string) {
    return this.decision(id, 'submit', { comment: comment || null }, 'Falha ao enviar para aprovação');
  }

  withdraw(id: number, comment?: string) {
    return this.decision(id, 'withdraw', { comment: comment || null }, 'Falha ao voltar para rascunho');
  }

  approve(id: number, comment?: string) {
    return this.decision(id, 'approve', { comment: comment || null }, 'Falha ao aprovar');
  }

  requestChanges(id: number, comment: string) {
    return this.decision(id, 'request-changes', { comment }, 'Falha ao pedir ajustes');
  }

  markPublished(id: number, permalinks: Record<number, string>, comment?: string) {
    return this.decision(id, 'mark-published', { permalinks, comment: comment || null }, 'Falha ao marcar como publicado');
  }

  setGridHidden(id: number, hidden: boolean) {
    return this.decision(id, 'grid-visibility', { hidden }, 'Falha ao alterar a Grade');
  }

  comment(id: number, body: string) {
    return this.decision(id, 'comments', { body }, 'Falha ao comentar');
  }

  publishNow(id: number) {
    return this.decision(id, 'publish-now', {}, 'Falha ao pedir a publicação')
  }

  retryTarget(id: number, targetId: number) {
    return this.decision(id, `targets/${targetId}/retry`, {}, 'Falha ao pedir nova tentativa')
  }

  // ------------------------------------------------------------ contas

  /** GET /social/oauth/:network/start → URL de login da rede (o navegador vai para lá). */
  async oauthStart(network: OAuthNetwork): Promise<string> {
    const res = await (await this.api()).get(`/social/oauth/${network}/start`);
    return (await readData<{ url: string }>(res, 'Falha ao iniciar a conexão')).url;
  }

  async refreshAccount(id: number): Promise<SocialAccount> {
    const res = await (await this.api()).post(`/social/accounts/${id}/refresh`);
    return readData<SocialAccount>(res, 'Falha ao renovar o token');
  }

  async syncFeed(id: number): Promise<number> {
    const res = await (await this.api()).post(`/social/accounts/${id}/sync-feed`);
    return (await readData<{ items: number }>(res, 'Falha ao ler o feed')).items;
  }

  async listConnectionReports(network?: OAuthNetwork): Promise<ConnectionReport[]> {
    const res = await (await this.api()).get(`/social/accounts/connection-reports${toQuery({ network })}`);
    return readList<ConnectionReport>(res, 'Falha ao carregar os diagnósticos');
  }

  async getConnectionReport(id: number): Promise<ConnectionReport> {
    const res = await (await this.api()).get(`/social/accounts/connection-reports/${id}`);
    return readData<ConnectionReport>(res, 'Falha ao carregar o diagnóstico');
  }

  async listAccounts(): Promise<SocialAccount[]> {
    const res = await (await this.api()).get('/social/accounts');
    return readList<SocialAccount>(res, 'Falha ao carregar as contas');
  }

  async manageAccounts(): Promise<SocialAccount[]> {
    const res = await (await this.api()).get('/social/accounts/manage');
    return readList<SocialAccount>(res, 'Falha ao carregar as contas');
  }

  async saveAccount(id: number | null, req: SaveAccountRequest): Promise<SocialAccount> {
    const api = await this.api();
    const res = id ? await api.put(`/social/accounts/${id}`, req) : await api.post('/social/accounts', req);
    return readData<SocialAccount>(res, 'Falha ao salvar a conta');
  }

  // ------------------------------------------------------------ pilares, hashtags, ideias

  async listPillars(includeInactive = false): Promise<Pillar[]> {
    const res = await (await this.api()).get(`/social/pillars${toQuery({ include_inactive: includeInactive })}`);
    return readList<Pillar>(res, 'Falha ao carregar os pilares');
  }

  async savePillar(id: number | null, req: SavePillarRequest): Promise<Pillar> {
    const api = await this.api();
    const res = id ? await api.put(`/social/pillars/${id}`, req) : await api.post('/social/pillars', req);
    return readData<Pillar>(res, 'Falha ao salvar o pilar');
  }

  async listHashtagGroups(includeInactive = false): Promise<HashtagGroup[]> {
    const res = await (await this.api()).get(`/social/hashtag-groups${toQuery({ include_inactive: includeInactive })}`);
    return readList<HashtagGroup>(res, 'Falha ao carregar os grupos de hashtags');
  }

  async saveHashtagGroup(id: number | null, req: SaveHashtagGroupRequest): Promise<HashtagGroup> {
    const api = await this.api();
    const res = id ? await api.put(`/social/hashtag-groups/${id}`, req) : await api.post('/social/hashtag-groups', req);
    return readData<HashtagGroup>(res, 'Falha ao salvar o grupo de hashtags');
  }

  async deleteHashtagGroup(id: number): Promise<void> {
    const res = await (await this.api()).delete(`/social/hashtag-groups/${id}`);
    await ensureOk(res, 'Falha ao excluir o grupo');
  }

  async listIdeas(f: { kind?: IdeaKind | ''; network?: Network | ''; search?: string; includeArchived?: boolean } = {}): Promise<Idea[]> {
    const q = toQuery({ kind: f.kind, network: f.network, search: f.search, include_archived: f.includeArchived });
    const res = await (await this.api()).get(`/social/ideas${q}`);
    return readList<Idea>(res, 'Falha ao carregar as ideias');
  }

  async saveIdea(id: number | null, req: SaveIdeaRequest): Promise<Idea> {
    const api = await this.api();
    const res = id ? await api.put(`/social/ideas/${id}`, req) : await api.post('/social/ideas', req);
    return readData<Idea>(res, 'Falha ao salvar a ideia');
  }

  async deleteIdea(id: number): Promise<void> {
    const res = await (await this.api()).delete(`/social/ideas/${id}`);
    await ensureOk(res, 'Falha ao excluir a ideia');
  }

  async ideaToPost(id: number): Promise<SocialPost> {
    const res = await (await this.api()).post(`/social/ideas/${id}/to-post`);
    return normalize(await readData<SocialPost>(res, 'Falha ao criar o post a partir da ideia'));
  }
}

const socialService = new SocialService();
export default socialService;
